/**
 * "Enter your recovery code" — the in-app answer to a locked vault.
 *
 * Signing out wipes the unwrapped master key from the device on purpose.
 * Letter-first users come back through Login without passing the unlock
 * screen (letters need no key), so the first thing that does need it — a
 * recording, a document — meets VaultLockedError. Instead of a generic
 * failure, offer the unlock screen and come back to where they were.
 */
import { Alert, Platform } from 'react-native';

import { RecoveryCodeRequiredError, VaultLockedError } from '../encryption/MasterKeyService';
import { navigationRef } from '../navigationService';

/** Where UnlockVaultScreen sends the user after a successful unlock. */
export interface UnlockReturnTo {
  root: string;
  routes: { name: string; params?: Record<string, unknown> }[];
}

export function isVaultLockedError(error: unknown): boolean {
  return (
    error instanceof VaultLockedError ||
    (error instanceof Error && error.name === 'VaultLockedError')
  );
}

export function isRecoveryCodeRequiredError(error: unknown): boolean {
  return (
    error instanceof RecoveryCodeRequiredError ||
    (error instanceof Error && error.name === 'RecoveryCodeRequiredError')
  );
}

/**
 * "Set a Recovery Code" — asked the first time an account without one saves
 * a document (2026-09-25) or a login (K5). After the code is set, the user
 * goes back to `returnTo` and saves again.
 */
export function promptToSetRecoveryCode(
  returnTo: UnlockReturnTo | undefined,
  what: 'documents' | 'logins' = 'documents',
): void {
  const copy =
    what === 'logins'
      ? {
          title: 'Protect your saved logins with a Recovery Code',
          body:
            'Without a Recovery Code, FinalKey\'s server can read your saved logins. ' +
            'Set one now and only you will be able to. Then come back and save again.\n\n' +
            'Write it down somewhere safe: if it is lost, FinalKey cannot recover your logins or documents.',
        }
      : {
          title: 'Protect your documents with a Recovery Code',
          body:
            'Without a Recovery Code, FinalKey\'s server can read your documents. ' +
            'Set one now and only you will be able to. Then save your document again.\n\n' +
            'Write it down somewhere safe: if it is lost, FinalKey cannot recover your documents.',
        };
  Alert.alert(
    copy.title,
    copy.body,
    [
      { text: 'Not now', style: 'cancel' },
      {
        text: 'Set Recovery Code',
        onPress: () => {
          if (!navigationRef.isReady()) return;
          (navigationRef as any).navigate('Profile', {
            screen: 'SetRecoveryCodeIntro',
            params: { returnTo },
          });
        },
      },
    ],
  );
}

/**
 * For a failed document save: offers the Recovery Code or unlock flow when
 * that is what's missing. Returns true when it handled the error.
 */
export function promptForDocumentKey(error: unknown, returnTo: UnlockReturnTo): boolean {
  if (isRecoveryCodeRequiredError(error)) {
    promptToSetRecoveryCode(returnTo);
    return true;
  }
  if (isVaultLockedError(error)) {
    promptToUnlockVault('your documents are', returnTo);
    return true;
  }
  return false;
}

/**
 * The screen the person is on now, as an UnlockReturnTo, so a prompt raised
 * from inside a service (K5: a saved login met without the key) can bring
 * them back. Undefined when navigation is not ready or the route has no
 * nested stack; UnlockVaultScreen then goes to Dashboard.
 */
export function currentRouteAsReturnTo(): UnlockReturnTo | undefined {
  try {
    if (!navigationRef.isReady()) return undefined;
    const state = navigationRef.getRootState();
    const root = state?.routes?.[state.index ?? 0];
    const nested = (root as any)?.state;
    if (!root || !nested?.routes?.length) return undefined;
    const upTo = (nested.index ?? nested.routes.length - 1) + 1;
    return {
      root: root.name,
      routes: nested.routes.slice(0, upTo).map((r: any) => ({ name: r.name, params: r.params })),
    };
  } catch {
    return undefined;
  }
}

export function promptToUnlockVault(what: string, returnTo?: UnlockReturnTo): void {
  Alert.alert(
    'Your recovery code is needed',
    Platform.OS === 'web'
      ? `A browser never keeps your vault key, so ${what} locked. Enter your recovery code to unlock it for this visit.`
      : `This phone was signed out, so ${what} locked. Enter your recovery code to unlock it here.`,
    [
      { text: 'Not now', style: 'cancel' },
      {
        text: 'Enter code',
        onPress: () => {
          if (!navigationRef.isReady()) return;
          (navigationRef as any).navigate('Auth', {
            screen: 'UnlockVault',
            params: { returnTo },
          });
        },
      },
    ],
  );
}
